import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Box, Button, FormControlLabel, Switch, Tab, Tabs, Typography } from "@mui/material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import { Container } from "@/globalStyles";
import { ClipPage } from "@/components/Clips/Clip.elements";
import { StatsSection, StatsTopBar, StatsTitleLeft } from "@/components/Stats/Counter/CountersBlock.elements";
import { useAuthState, useTutorialsState, useTutorialsActions } from "@/store";
import { TUTORIALS_NEWEST_FIRST, getTutorial } from "./registry";
import { TutorialCard } from "./TutorialCard";
import { TutorialDialog } from "./TutorialDialog";
import { TutorialResultsTab } from "./TutorialResultsTab";
import { getTutorialProgress } from "./decideTutorialPrompt";
import { useStartTutorial } from "./useStartTutorial";

/**
 * /tutorials — overview grid of tutorials. Clicking a card opens the tutorial
 * popup (?open=<id>); ?tour=1 additionally starts its tour once the popup's
 * playground is mounted (this is how the post-login prompt hands over).
 */
export const TutorialsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tutorialsState = useTutorialsState();
  const { setSuppressPrompts, fetchTutorialsConfig, updateTutorialsConfig } = useTutorialsActions();
  const startTutorial = useStartTutorial();
  const { currentUser } = useAuthState();
  const isAdmin = currentUser?.role === "admin";
  const [adminMode, setAdminMode] = useState(false);
  // Admin mode has two tabs: manage the tutorials (hide / popup / mandatory) and see who passed what.
  const [adminTab, setAdminTab] = useState(0);

  useEffect(() => {
    if (!tutorialsState.configLoaded) fetchTutorialsConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const config = useMemo(() => tutorialsState.config || { hidden: [], noPrompt: [] }, [tutorialsState.config]);
  const hiddenSet = useMemo(() => new Set(config.hidden || []), [config.hidden]);
  const noPromptSet = useMemo(() => new Set(config.noPrompt || []), [config.noPrompt]);
  const mandatorySet = useMemo(() => new Set(config.mandatory || []), [config.mandatory]);
  const visibleTutorials = useMemo(
    () => (isAdmin && adminMode ? TUTORIALS_NEWEST_FIRST : TUTORIALS_NEWEST_FIRST.filter((t) => !hiddenSet.has(t.id))),
    [isAdmin, adminMode, hiddenSet]
  );

  const toggleInList = useCallback(
    (list, id) => {
      const current = new Set(config[list] || []);
      if (current.has(id)) current.delete(id);
      else current.add(id);
      updateTutorialsConfig({ ...config, [list]: Array.from(current) });
    },
    [config, updateTutorialsConfig]
  );

  const openId = searchParams.get("open");
  const autoStartTour = searchParams.get("tour") === "1";
  const openTutorial = useMemo(() => (openId ? getTutorial(openId) : null), [openId]);

  const openDialog = useCallback(
    (tutorial, { tour = false } = {}) => {
      const next = new URLSearchParams(searchParams);
      next.set("open", tutorial.id);
      if (tour) next.set("tour", "1");
      else next.delete("tour");
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const closeDialog = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    next.delete("tour");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const consumeAutoStart = useCallback(() => {
    if (!searchParams.get("tour")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("tour");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <ClipPage data-testid="tutorials-page">
      <Container>
        <StatsSection>
          <StatsTopBar>
            <Box>
              <StatsTitleLeft>Tutorials</StatsTitleLeft>
              <Typography sx={{ mt: 0.5, fontSize: 14, color: "rgba(247,250,250,0.7)", maxWidth: 760 }}>
                Short guided tours and articles about uploading — how it survives bad internet, what every
                status means, how to continue on another device, and when Cancel is (and isn&apos;t) the right
                button. Open a tutorial to read it and try things in a safe playground.
              </Typography>
            </Box>
          </StatsTopBar>

          {isAdmin && (
            <Box
              data-testid="tutorials-admin-bar"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                mb: 2,
                px: 2,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${adminMode ? "rgba(245,199,84,0.55)" : "rgba(255,255,255,0.12)"}`,
                background: adminMode ? "rgba(245,199,84,0.08)" : "rgba(255,255,255,0.03)",
              }}
            >
              <AdminPanelSettingsRoundedIcon sx={{ color: "#F5C754" }} />
              <FormControlLabel
                sx={{ m: 0, "& .MuiTypography-root": { fontSize: 14, fontWeight: 600, color: "#F7FAFA" } }}
                control={
                  <Switch
                    checked={adminMode}
                    onChange={(e) => setAdminMode(e.target.checked)}
                    data-testid="tutorials-admin-mode"
                    sx={{ "& .Mui-checked": { color: "#F5C754" }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: "#F5C754 !important" } }}
                  />
                }
                label="Admin mode"
              />
              <Typography sx={{ fontSize: 13, color: "rgba(247,250,250,0.7)" }}>
                {adminMode
                  ? "Per tutorial: hide it from contributors, stop it popping up after login, or make it mandatory — a mandatory tutorial keeps being offered until the person completes it, even if they turned tips off. Changes apply to everyone."
                  : "Switch on to hide tutorials, turn off their post-login popup, or mark one as mandatory for all users."}
              </Typography>
              {tutorialsState.configSaving && (
                <Typography sx={{ fontSize: 12, color: "#F5C754" }}>Saving…</Typography>
              )}
            </Box>
          )}

          {isAdmin && adminMode && (
            <Tabs
              value={adminTab}
              onChange={(_e, v) => setAdminTab(v)}
              data-testid="tutorials-admin-tabs"
              sx={{
                mb: 1,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                "& .MuiTab-root": { color: "#f7fafaa6", fontSize: "13px", fontWeight: 700, minHeight: 42, textTransform: "none" },
                "& .Mui-selected": { color: "#fff" },
                "& .MuiTabs-indicator": { backgroundColor: "#F5C754" },
              }}
            >
              <Tab label="Manage tutorials" data-testid="tutorials-admin-tab-manage" />
              <Tab label="Results" data-testid="tutorials-admin-tab-results" />
            </Tabs>
          )}

          {isAdmin && adminMode && adminTab === 1 && <TutorialResultsTab />}

          {!(isAdmin && adminMode && adminTab === 1) && tutorialsState.preferences?.suppressPrompts && (
            <Alert
              severity="info"
              variant="outlined"
              data-testid="tutorials-suppressed-alert"
              sx={{ mb: 2, borderColor: "rgba(139,148,255,0.35)", color: "rgba(247,250,250,0.9)" }}
              action={
                <Button
                  size="small"
                  onClick={() => setSuppressPrompts(false)}
                  data-testid="tutorials-enable-prompts"
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Turn on
                </Button>
              }
            >
              Tips about new features are turned off. You can still open any tutorial from here.
            </Alert>
          )}

          {!(isAdmin && adminMode && adminTab === 1) && (
          <Box
            sx={{
              display: "grid",
              // One card per row on phones (also large ones / landscape); two from tablets, three on wide desktops.
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
              gap: 2,
              pt: 1,
              pb: 4,
              width: "100%",
              maxWidth: 1240,
            }}
            data-testid="tutorials-grid"
          >
            {visibleTutorials.map((tutorial) => (
              <TutorialCard
                key={tutorial.id}
                tutorial={tutorial}
                progress={getTutorialProgress(tutorial, tutorialsState)}
                mandatory={mandatorySet.has(tutorial.id)}
                onOpen={() => openDialog(tutorial)}
                onStartTour={() =>
                  tutorial.mode === "playground"
                    ? openDialog(tutorial, { tour: true })
                    : startTutorial(tutorial, { source: "page" })
                }
                admin={
                  isAdmin && adminMode
                    ? {
                        hidden: hiddenSet.has(tutorial.id),
                        noPrompt: noPromptSet.has(tutorial.id),
                        mandatory: mandatorySet.has(tutorial.id),
                        saving: tutorialsState.configSaving,
                        onToggleHidden: () => toggleInList("hidden", tutorial.id),
                        onToggleNoPrompt: () => toggleInList("noPrompt", tutorial.id),
                        onToggleMandatory: () => toggleInList("mandatory", tutorial.id),
                      }
                    : null
                }
              />
            ))}
          </Box>
          )}
        </StatsSection>
      </Container>

      <TutorialDialog
        // Keyed by id so switching tutorials remounts the playground (fresh
        // sandbox state) and stops any tour still running for the previous one.
        key={openTutorial?.id || "none"}
        open={!!openTutorial}
        tutorial={openTutorial}
        progress={openTutorial ? getTutorialProgress(openTutorial, tutorialsState) : "NEW"}
        autoStartTour={autoStartTour}
        onAutoStartConsumed={consumeAutoStart}
        onClose={closeDialog}
      />
    </ClipPage>
  );
};

export default TutorialsPage;
